import React, { useEffect } from "react";
import { Form, Row } from "react-bootstrap";
import PropTypes from 'prop-types';
import { object, string } from 'yup';
import { FormControl, FormLabel } from '@material-ui/core';
import { CheckboxInput, RadioInput, StyledRadioInput } from "../../../Helpers/Inputs";
import { ConvertStringToBoolean, ConvertBooleanToString } from "../../../Helpers/Formatters/ConvertBoolean";
import Age from '../../../Helpers/Dates/Age';
import Step5Icon from '../icons/step_5.svg';
import IconHolder from "../../../Helpers/IconHolder";
import { getLabelForAids, getLabelForDiabetes, getLabelForMedicalIssues, nicotineUsageRadios } from "../Helpers/MedicalHistoryData";

export default function MedicalHistory(props) {
  const { register, errors, setValue, triggerValidation } = props;

  const noneOfTheAboveDisabled = (
    props.formState.alcoholOrDrugAbuse === "true" ||
    props.formState.heartDisease === "true" ||
    props.formState.stroke === "true" ||
    props.formState.lungDisease === "true" ||
    props.formState.depressionBipolar === "true" ||
    props.formState.cognitiveDisorder === "true" ||
    props.formState.muscularDystrophy === "true" ||
    props.formState.kidneyLiverDisease === "true" ||
    props.formState.leukemia === "true" ||
    props.formState.arthritis === "true" ||
    props.formState.sleepApnea === "true"
  );

  const state = props.basicInfo.state;
  const insuredAge = Age(props.basicInfo.birthdate);

  // uncheck "none" if it becomes disabled
  useEffect(() => {
    if (noneOfTheAboveDisabled && props.formState.none === "true") {
      props.onFieldChange('none', "false");
    } else if (noneOfTheAboveDisabled && props.formState.none !== "true") {
      triggerValidation('none');
    }
  }, [noneOfTheAboveDisabled]);

  useEffect(() => {
    register({ name: 'nicotineUse' });
    setValue('nicotineUse', props.formState.nicotineUse);

    register({ name: 'useRecreationalMarijuana' });
    setValue('useRecreationalMarijuana', props.formState.useRecreationalMarijuana);

    register({ name: 'illegalDrugUse' });
    setValue('illegalDrugUse', props.formState.illegalDrugUse);

    register({ name: 'illegalPrescriptionDrugUse' });
    setValue('illegalPrescriptionDrugUse', props.formState.illegalPrescriptionDrugUse);

    register({ name: 'diabetesPriorToAge40' });
    setValue('diabetesPriorToAge40', props.formState.diabetesPriorToAge40);

    register({ name: 'diabetes' });
    setValue('diabetes', props.formState.diabetes);

    register({ name: 'insuredAge' });
    setValue('insuredAge', insuredAge);

    register({ name: 'insulinDependent' });
    setValue('insulinDependent', props.formState.insulinDependent);

    register({ name: 'complicationsfromDiabetes' });
    setValue('complicationsfromDiabetes', props.formState.complicationsfromDiabetes);

    register({ name: 'aids' });
    setValue('aids', props.formState.aids);

    register({ name: 'incompleteFollowup' });
    setValue('incompleteFollowup', props.formState.incompleteFollowup);

  }, [register]);

  useEffect(() => {
    if (props.formState.useRecreationalMarijuana !== "true") {
      props.onFieldChange('marijuanaFrequency', "");
    }
  }, [props.formState.useRecreationalMarijuana]);

  const handleValueChange = (field, value) => {
    if (field === 'nicotineUse') {
      const form = { ...props.fullFormState };
      form.steps.basicInfo.tobacco = value;
      form.steps.medicalHistory.nicotineUse = value;
      setValue(field, value);
      props.onMultiFieldChange(form);
      triggerValidation(field);
    }
    else {
      setValue(field, value);
      props.onFieldChange(field, value);
      triggerValidation(field);
    }
  };

  const handleCheckBoxValue = (field, value) => {
    props.onFieldChange(field, ConvertBooleanToString(value));
  };

  return (
    <React.Fragment>
      <Row className="justify-content-center">
        <IconHolder icon={Step5Icon} viewBox="0 0 25 25" color='primary' />
      </Row>
      <h4 className="headerTitle">Reviewing Your Medical History</h4>
      <h5 className="headerSubtitle">This information replaces taking a medical exam.</h5>
      <Form.Group>
        <RadioInput
          name="nicotineUse"
          id="nicotineUse"
          label="When was the last time you used any nicotine products, including electronic cigarettes or vaping devices?"
          value={props.formState.nicotineUse}
          radios={nicotineUsageRadios}
          onChange={e => handleValueChange('nicotineUse', e.target.value)}
          errors={errors.nicotineUse}
          inputRef={register}
        />
      </Form.Group>

      <Form.Group>
        <StyledRadioInput
          name="useRecreationalMarijuana"
          id="useRecreationalMarijuana"
          label="Do you use Marijuana recreationally?"
          value={props.formState.useRecreationalMarijuana}
          options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
          onChange={e => handleValueChange('useRecreationalMarijuana', e)}
          errors={errors.useRecreationalMarijuana}
        />
      </Form.Group>

      {props.formState.useRecreationalMarijuana === "true" &&
        <Form.Group>
          <RadioInput
            name="marijuanaFrequency"
            id="marijuanaFrequency"
            label="What is the average frequency of your use?"
            value={props.formState.marijuanaFrequency}
            radios={[{ value: "Once a week", label: "Once a week" }, { value: "2-3 times a week", label: "2-3 times a week" }, { value: "More than 3 times a week", label: "More than 3 times a week" }]}
            onChange={e => handleValueChange('marijuanaFrequency', e.target.value)}
            errors={errors.marijuanaFrequency}
            inputRef={register}
          />
        </Form.Group>
      }

      <Form.Group>
        <StyledRadioInput
          name="illegalDrugUse"
          id="illegalDrugUse"
          label="In the past 5 years, have you used cocaine, opiates, methamphetamines or any illegal drug?"
          value={props.formState.illegalDrugUse}
          options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
          onChange={e => handleValueChange('illegalDrugUse', e)}
          errors={errors.illegalDrugUse}
        />
      </Form.Group>

      <Form.Group>
        <StyledRadioInput
          name="illegalPrescriptionDrugUse"
          id="illegalPrescriptionDrugUse"
          label="In the past 5 years, have you used prescription drugs other than as prescribed by a licensed member of the medical profession?"
          value={props.formState.illegalPrescriptionDrugUse}
          options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
          onChange={e => handleValueChange('illegalPrescriptionDrugUse', e)}
          errors={errors.illegalPrescriptionDrugUse}
        />
      </Form.Group>

      <FormControl component="fieldset">
        <FormLabel component="legend">{getLabelForMedicalIssues(state)}:</FormLabel>
        <Form.Group className="checkboxes">
          <CheckboxInput
            name="alcoholOrDrugAbuse"
            id="alcoholOrDrugAbuse"
            checked={ConvertStringToBoolean(props.formState.alcoholOrDrugAbuse)}
            onChange={e => handleCheckBoxValue('alcoholOrDrugAbuse', e.target.checked)}
            errors={errors.alcoholOrDrugAbuse}
            inputRef={register}
            label="Alcohol or drug abuse?"
          />
          <CheckboxInput
            name="heartDisease"
            id="heartDisease"
            checked={ConvertStringToBoolean(props.formState.heartDisease)}
            onChange={e => handleCheckBoxValue('heartDisease', e.target.checked)}
            errors={errors.heartDisease}
            inputRef={register}
            label="Heart disease, heart surgery, heart failure, irregular heart rate?"
          />
          <CheckboxInput
            name="stroke"
            id="stroke"
            checked={ConvertStringToBoolean(props.formState.stroke)}
            onChange={e => handleCheckBoxValue('stroke', e.target.checked)}
            errors={errors.stroke}
            inputRef={register}
            label="Stroke, mini-stroke, aneurysm?"
          />
          <CheckboxInput
            name="lungDisease"
            id="lungDisease"
            checked={ConvertStringToBoolean(props.formState.lungDisease)}
            onChange={e => handleCheckBoxValue('lungDisease', e.target.checked)}
            errors={errors.lungDisease}
            inputRef={register}
            label="Chronic lung disease, emphysema, chronic bronchitis, cystic fibrosis, asthma requiring a daily steroid?"
          />
          <CheckboxInput
            name="depressionBipolar"
            id="depressionBipolar"
            checked={ConvertStringToBoolean(props.formState.depressionBipolar)}
            onChange={e => handleCheckBoxValue('depressionBipolar', e.target.checked)}
            errors={errors.depressionBipolar}
            inputRef={register}
            label="Major depression, bipolar, schizophrenia, eating disorder?"
          />
          <CheckboxInput
            name="cognitiveDisorder"
            id="cognitiveDisorder"
            checked={ConvertStringToBoolean(props.formState.cognitiveDisorder)}
            onChange={e => handleCheckBoxValue('cognitiveDisorder', e.target.checked)}
            errors={errors.cognitiveDisorder}
            inputRef={register}
            label="Cognitive disorder, Alzheimer’s, dementia, Parkinson’s, amyotrophic lateral sclerosis (ALS), seizure disorder?"
          />
          <CheckboxInput
            name="muscularDystrophy"
            id="muscularDystrophy"
            checked={ConvertStringToBoolean(props.formState.muscularDystrophy)}
            onChange={e => handleCheckBoxValue('muscularDystrophy', e.target.checked)}
            errors={errors.muscularDystrophy}
            inputRef={register}
            label="Muscular dystrophy, multiple sclerosis, cerebral palsy, paralysis?"
          />
          <CheckboxInput
            name="kidneyLiverDisease"
            id="kidneyLiverDisease"
            checked={ConvertStringToBoolean(props.formState.kidneyLiverDisease)}
            onChange={e => handleCheckBoxValue('kidneyLiverDisease', e.target.checked)}
            errors={errors.kidneyLiverDisease}
            inputRef={register}
            label="Kidney disease, liver disease, blood disorder?"
          />
          <CheckboxInput
            name="leukemia"
            id="leukemia"
            checked={ConvertStringToBoolean(props.formState.leukemia)}
            onChange={e => handleCheckBoxValue('leukemia', e.target.checked)}
            errors={errors.leukemia}
            inputRef={register}
            label="Leukemia, melanoma, brain tumor or cancer other than basal cell skin cancer (BCC)?"
          />
          <CheckboxInput
            name="arthritis"
            id="arthritis"
            checked={ConvertStringToBoolean(props.formState.arthritis)}
            onChange={e => handleCheckBoxValue('arthritis', e.target.checked)}
            errors={errors.arthritis}
            inputRef={register}
            label="Rheumatoid arthritis, lupus, scleroderma?"
          />
          <CheckboxInput
            name="sleepApnea"
            id="sleepApnea"
            checked={ConvertStringToBoolean(props.formState.sleepApnea)}
            onChange={e => handleCheckBoxValue('sleepApnea', e.target.checked)}
            errors={errors.sleepApnea}
            inputRef={register}
            label="Sleep apnea, which is not treated by CPAP or BiPAP therapy?"
          />
          <CheckboxInput
            name="none"
            id="none"
            checked={ConvertStringToBoolean(props.formState.none)}
            onChange={e => handleCheckBoxValue('none', e.target.checked)}
            errors={errors.none}
            inputRef={register}
            label="None of the above"
            disabled={noneOfTheAboveDisabled}
          />
        </Form.Group>
      </FormControl>

      <Form.Group>
        <StyledRadioInput
          name="diabetes"
          id="diabetes"
          label={getLabelForDiabetes(state)}
          value={props.formState.diabetes}
          options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
          onChange={e => handleValueChange('diabetes', e)}
          errors={errors.diabetes}
        />
      </Form.Group>

      {props.formState.diabetes === "true" &&
        <React.Fragment>
          <Form.Group>
            <StyledRadioInput
              name="diabetesPriorToAge40"
              id="diabetesPriorToAge40"
              label="Was it diagnosed prior to age 40?"
              value={props.formState.diabetesPriorToAge40}
              options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
              onChange={e => handleValueChange('diabetesPriorToAge40', e)}
              errors={errors.diabetesPriorToAge40}
            />
          </Form.Group>

          <Form.Group>
            <StyledRadioInput
              name="insulinDependent"
              id="insulinDependent"
              label="Are you insulin dependent?"
              value={props.formState.insulinDependent}
              options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
              onChange={e => handleValueChange('insulinDependent', e)}
              errors={errors.insulinDependent}
            />
          </Form.Group>

          <Form.Group>
            <StyledRadioInput
              name="complicationsfromDiabetes"
              id="complicationsfromDiabetes"
              label="Have you had diabetic complications such as nerve pain, skin ulcers, vision, or kidney problems?"
              value={props.formState.complicationsfromDiabetes}
              options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
              onChange={e => handleValueChange('complicationsfromDiabetes', e)}
              errors={errors.complicationsfromDiabetes}
            />
          </Form.Group>
        </React.Fragment>
      }

      <Form.Group>
        <StyledRadioInput
          name="aids"
          id="aids"
          label={getLabelForAids(state)}
          value={props.formState.aids}
          options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
          onChange={e => handleValueChange('aids', e)}
          errors={errors.aids}
        />
      </Form.Group>

      <Form.Group>
        <StyledRadioInput
          name="incompleteFollowup"
          id="incompleteFollowup"
          label="In the past 2 years, has a licensed member of the medical profession recommended additional follow-up treatment which has not been completed yet?"
          value={props.formState.incompleteFollowup}
          options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
          onChange={e => handleValueChange('incompleteFollowup', e)}
          errors={errors.incompleteFollowup}
        />
      </Form.Group>
    </React.Fragment>
  );
}

MedicalHistory.propTypes = {
  formState: PropTypes.object.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  basicInfo: PropTypes.object,
  fullFormState: PropTypes.object,
  register: PropTypes.func,
  errors: PropTypes.object,
  triggerValidation: PropTypes.func,
  onMultiFieldChange: PropTypes.func,
  setValue: PropTypes.func
};

MedicalHistory.ValidationSchema = object().shape({
  nicotineUse: string().required('This field is required.'),
  useRecreationalMarijuana: string().required('This field is required.'),
  marijuanaFrequency: string().when("useRecreationalMarijuana", {
    is: "true",
    then: string().required('This field is required.'),
    otherwise: string().notRequired()
  }),
  illegalDrugUse: string().required('This field is required.'),
  illegalPrescriptionDrugUse: string().required('This field is required.'),

  alcoholOrDrugAbuse: string().required('This field is required.'),
  heartDisease: string().required('This field is required.'),
  stroke: string().required('This field is required.'),
  lungDisease: string().required('This field is required.'),
  depressionBipolar: string().required('This field is required.'),
  cognitiveDisorder: string().required('This field is required.'),
  muscularDystrophy: string().required('This field is required.'),
  kidneyLiverDisease: string().required('This field is required.'),
  leukemia: string().required('This field is required.'),
  arthritis: string().required('This field is required.'),
  sleepApnea: string().required('This field is required.'),
  none: string().required()
    .when(['alcoholOrDrugAbuse', 'heartDisease', 'stroke', 'lungDisease', 'depressionBipolar', 'cognitiveDisorder', 'muscularDystrophy', 'kidneyLiverDisease', 'leukemia', 'arthritis', 'sleepApnea'], {
      is: (alcoholOrDrugAbuse, heartDisease, stroke, lungDisease, depressionBipolar, cognitiveDisorder, muscularDystrophy, kidneyLiverDisease, leukemia, arthritis, sleepApnea) => checkBoxValidation(alcoholOrDrugAbuse, heartDisease, stroke, lungDisease, depressionBipolar, cognitiveDisorder, muscularDystrophy, kidneyLiverDisease, leukemia, arthritis, sleepApnea),
      then: string().matches(/^true/, 'Please check one checkbox.'),
      otherwise: string().required('This field is required.')
    }),

  diabetes: string().required('This field is required.'),
  diabetesPriorToAge40: string().when("diabetes", {
    is: "true",
    then: string().when("insuredAge", {
      is: (insuredAge) => insuredAge < 40,
      then: string().required('This field is required.').matches(/^true/, "Invalid answer. Insured is under 40 years of age."),
      otherwise: string().required('This field is required.'),
    }),
    otherwise: string().notRequired()
  }),
  insulinDependent: string().when("diabetes", {
    is: "true",
    then: string().required('This field is required.'),
    otherwise: string().notRequired()
  }),
  complicationsfromDiabetes: string().when("diabetes", {
    is: "true",
    then: string().required('This field is required.'),
    otherwise: string().notRequired()
  }),
  aids: string().required('This field is required.'),
  incompleteFollowup: string().required('This field is required.'),
});

function checkBoxValidation(alcoholOrDrugAbuse, heartDisease, stroke, lungDisease, depressionBipolar, cognitiveDisorder, muscularDystrophy, kidneyLiverDisease, leukemia, arthritis, sleepApnea) {
  if (alcoholOrDrugAbuse === "false" && heartDisease === "false" && stroke === "false" && lungDisease === "false" && depressionBipolar === "false" && cognitiveDisorder === "false" && muscularDystrophy === "false" && kidneyLiverDisease === "false" && leukemia === "false" && arthritis === "false" && sleepApnea === "false")
    {return true;}
  return false;
}

MedicalHistory.DefaultFormState = (prefill = false, mctData = null) => {
  return {
    nicotineUse: prefill ? "Never" : (mctData && mctData.tobacco) || '',
    useRecreationalMarijuana: prefill ? "false" : "",
    marijuanaFrequency: prefill ? "Once a week" : "",
    illegalDrugUse: prefill ? "false" : "",
    illegalPrescriptionDrugUse: prefill ? "false" : "",
    alcoholOrDrugAbuse: "false",
    heartDisease: "false",
    stroke: "false",
    lungDisease: "false",
    depressionBipolar: "false",
    cognitiveDisorder: "false",
    muscularDystrophy: "false",
    kidneyLiverDisease: "false",
    leukemia: "false",
    arthritis: "false",
    sleepApnea: "false",
    none: prefill ? "true" : "false",
    diabetes: prefill ? "false" : "",
    diabetesPriorToAge40: prefill ? "false" : "",
    insulinDependent: prefill ? "false" : "",
    complicationsfromDiabetes: prefill ? "false" : "",
    aids: prefill ? "false" : "",
    incompleteFollowup: prefill ? "false" : "",
  };
};



// WEBPACK FOOTER //
// ./src/components/Quoters/AmericoLoanProtection/steps/MedicalHistory.js